import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { isStaffRole } from '@pazportal/types';
import { useAuth } from '../contexts/AuthContext';

/**
 * Shown only after the current profile has resolved without staff authority.
 * Recovered staff access returns to Admin without requiring another sign-in.
 */
export function UnauthorizedPage() {
  const { user, session, role, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const switchAccount = async () => {
    setBusy(true);
    setError(null);
    try {
      if (await signOut()) {
        navigate('/login', { replace: true });
      } else {
        setBusy(false);
      }
    } catch (e) {
      setBusy(false);
      setError(e instanceof Error ? e.message : 'Could not sign out. Try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface" role="status" aria-live="polite">
        <span className="text-sm text-text-muted">Checking your session</span>
      </div>
    );
  }
  if (!session) return <Navigate to="/login" replace />;
  if (isStaffRole(role)) return <Navigate to="/dashboard" replace />;

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-4">
      <div className="max-w-md text-center">
        <h1 className="text-4xl font-bold text-text">403</h1>
        <p className="text-sm text-text-muted mt-2">
          This workspace is for PazNetworks staff.
          {user?.email ? (
            <>
              {' '}
              <span className="font-medium text-text">{user.email}</span> is signed in
              {' '}without a current staff profile role. Ask an administrator to review access on the Roles page.
            </>
          ) : null}
        </p>
        <button
          type="button"
          onClick={switchAccount}
          disabled={busy}
          className="inline-flex items-center justify-center mt-5 min-h-[44px] px-4 rounded-md bg-primary text-text-inverse text-sm font-medium hover:bg-primary-hover disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {busy ? 'Signing out…' : 'Sign out and use a different account'}
        </button>
        {error && (
          <p role="alert" className="mt-3 text-sm text-notification">{error}</p>
        )}
      </div>
    </div>
  );
}
